import React, { useCallback } from 'react';
import type { ComponentType, FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { default as OldButton } from '@atlaskit/button/custom-theme-button';
import Button from '@atlaskit/button/default/button';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import Heading from '@atlaskit/heading/heading';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { FeedTypeSelector } from '../FeedTypeSelector/index.ts';
import { FeedType } from '../useFeedTypeSelector.ts';
import { FollowingFeedSortByValue, PopularFeedTimeGranularityValue } from '../Feed.tsx';

export const i18n: {
	feedTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	manageFeed: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	sortByRelevant: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	sortByRecent: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	day: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	week: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	twoWeeks: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	month: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	year: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	popularDay: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	popularWeek: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	popularTwoWeeks: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	popularMonth: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	popularYear: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	feedTitle: {
		id: 'feed.title',
		defaultMessage: 'Discover what’s happening',
		description:
			'Heading at the top of the feed page indicating it displays updates from followed people and spaces.',
	},
	manageFeed: {
		id: 'feed.header.manage.following',
		defaultMessage: 'Edit feed',
		description: 'Button text that opens a modal that allows the user to configure settings',
	},
	sortByRelevant: {
		id: 'feed.header.sortBy.following.relevant',
		defaultMessage: 'Sort by: Most relevant',
		description:
			'Following feed sorting option which will sort the feed by relevance, showing the most relevant first.',
	},
	sortByRecent: {
		id: 'feed.header.sortBy.following.recent',
		defaultMessage: 'Sort by: Last activity',
		description:
			'Following feed sorting option which will sort the feed by last activity date, showing the most recent first.',
	},
	day: {
		id: 'feed.popular.day',
		defaultMessage: 'Today',
		description: 'Popular Feed Time Range Option for past day',
	},
	week: {
		id: 'feed.popular.week',
		defaultMessage: 'Past 7 days',
		description: 'Popular Feed Time Range Option for past week',
	},
	twoWeeks: {
		id: 'feed.popular.twoWeeks',
		defaultMessage: 'Past 14 days',
		description: 'Popular Feed Time Range Option for past 2 weeks',
	},
	month: {
		id: 'feed.popular.month',
		defaultMessage: 'Past 30 days',
		description: 'Popular Feed Time Range Option for past month',
	},
	year: {
		id: 'feed.popular.year',
		defaultMessage: 'Past year',
		description: 'Popular Feed Time Range Option for past year',
	},
	popularDay: {
		id: 'feed.popular.dropdown.prefix.day',
		defaultMessage: 'Popular: Today',
		description:
			"Popular Feed Time Range Option for past day with 'Popular' prefix for when dropdown is closed",
	},
	popularWeek: {
		id: 'feed.popular.dropdown.prefix.week',
		defaultMessage: 'Popular: Past 7 days',
		description:
			"Popular Feed Time Range Option for past week with 'Popular' prefix for when dropdown is closed",
	},
	popularTwoWeeks: {
		id: 'feed.popular.dropdown.prefix.twoWeeks',
		defaultMessage: 'Popular: Past 14 days',
		description:
			"Popular Feed Time Range Option for past two weeks with 'Popular' prefix for when dropdown is closed",
	},
	popularMonth: {
		id: 'feed.popular.dropdown.prefix.month',
		defaultMessage: 'Popular: Past 30 days',
		description:
			"Popular Feed Time Range Option for past month with 'Popular' prefix for when dropdown is closed",
	},
	popularYear: {
		id: 'feed.popular.dropdown.prefix.year',
		defaultMessage: 'Popular: Past year',
		description:
			"Popular Feed Time Range Option for past year with 'Popular' prefix for when dropdown is closed",
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FeedTitleAndActions = styled.div({
	position: 'relative',
	justifyContent: 'space-between',
	display: 'flex',
	flexWrap: 'wrap',
	marginBottom: token('space.200'),
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectorAndActions = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	flex: 1,
	whiteSpace: 'nowrap',
	marginTop: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FeedTitleHeader = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.100'),
	height: token('space.400'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpotlightContainer = styled.div({
	position: 'absolute',
	top: '0px',
	left: token('space.negative.050'),
	height: '100%',
	width: '100%',
	padding: token('space.050'),
	pointerEvents: 'none',
});

const linkButtonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			fontWeight: token('font.weight.regular'),
		},
		spinnerStyles,
	};
};

enum FeedHeaderDropdownType {
	FOLLOWING_FEED_SORT_BY = 'followingSortBy',
	POPULAR_FEED_TIME_GRANULARITY = 'popularTimeGranularity',
}

type FeedActionsDropdownComponentProps = {
	openActivityFilterModal: () => void;
	renderPopularFeed: boolean;
	feedType: FeedType | string;
	setFeedType: (feedType: FeedType | string) => void;
	customPanels?: {
		title: string;
		key: string;
		icon?: ComponentType<NewCoreIconProps>;
		content: JSX.Element;
	}[];
	popularFeedPillRef: React.RefObject<HTMLButtonElement>;
	followingFeedPillRef: React.RefObject<HTMLButtonElement>;
	popularFeedTimeGranularity: PopularFeedTimeGranularityValue;
	handlePopularFeedTimeGranularityDropdownChange: (
		timeGranularity: PopularFeedTimeGranularityValue,
	) => void;
	followingFeedSortBy: FollowingFeedSortByValue;
	handleFollowingFeedSortByDropdownChange: (sortBy: FollowingFeedSortByValue) => void;
	hasFollowedAnyone?: boolean;
	hasResults?: boolean;
};

export const FeedHeader: FC<FeedActionsDropdownComponentProps> = ({
	openActivityFilterModal,
	renderPopularFeed,
	feedType,
	setFeedType,
	customPanels,
	popularFeedPillRef,
	followingFeedPillRef,
	popularFeedTimeGranularity,
	handlePopularFeedTimeGranularityDropdownChange,
	followingFeedSortBy,
	handleFollowingFeedSortByDropdownChange,
	hasFollowedAnyone,
	hasResults = true,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleManageButtonClick = useCallback(() => {
		openActivityFilterModal();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'item',
				actionSubjectId: 'editFeed',
				source: 'homeScreen',
			},
		}).fire();
	}, [createAnalyticsEvent, openActivityFilterModal]);

	const getPopularFeedTimeGranularityDropDownText = (): string => {
		switch (popularFeedTimeGranularity) {
			case PopularFeedTimeGranularityValue.Day:
				return formatMessage(i18n.popularDay);
			case PopularFeedTimeGranularityValue.Week:
				return formatMessage(i18n.popularWeek);
			case PopularFeedTimeGranularityValue.Month:
				return formatMessage(i18n.popularMonth);
			case PopularFeedTimeGranularityValue.Year:
				return formatMessage(i18n.popularYear);
			default:
				return formatMessage(i18n.popularTwoWeeks);
		}
	};

	const getFollowingFeedSortByDropDownText = (): string => {
		switch (followingFeedSortBy) {
			case FollowingFeedSortByValue.Recent:
				return formatMessage(i18n.sortByRecent);
			default:
				return formatMessage(i18n.sortByRelevant);
		}
	};

	const handleDropdownChange = useCallback(
		(
			type: FeedHeaderDropdownType,
			value: PopularFeedTimeGranularityValue | FollowingFeedSortByValue,
		) => {
			// if the value is the same as the current value, do nothing
			if (
				(type === FeedHeaderDropdownType.POPULAR_FEED_TIME_GRANULARITY &&
					value === popularFeedTimeGranularity) ||
				(type === FeedHeaderDropdownType.FOLLOWING_FEED_SORT_BY && value === followingFeedSortBy)
			) {
				return;
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'item',
					actionSubjectId: type,
					source: 'homeScreen',
					attributes: {
						[type === FeedHeaderDropdownType.POPULAR_FEED_TIME_GRANULARITY
							? 'timeGranularity'
							: 'sortBy']: value,
					},
				},
			}).fire();

			if (type === FeedHeaderDropdownType.POPULAR_FEED_TIME_GRANULARITY) {
				handlePopularFeedTimeGranularityDropdownChange(value as PopularFeedTimeGranularityValue);
			} else {
				handleFollowingFeedSortByDropdownChange(value as FollowingFeedSortByValue);
			}
		},
		[
			handleFollowingFeedSortByDropdownChange,
			handlePopularFeedTimeGranularityDropdownChange,
			createAnalyticsEvent,
			followingFeedSortBy,
			popularFeedTimeGranularity,
		],
	);

	const handleDropdownOpenChange = (type: FeedHeaderDropdownType, isOpen: boolean) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'toggled',
				actionSubject: 'dropdown',
				actionSubjectId: type,
				source: 'homeScreen',
				attributes: {
					isOpen,
				},
			},
		}).fire();
	};

	const handlePopularFeedTimeGranularityDropdownOpenChange = (isOpen: boolean) => {
		handleDropdownOpenChange(FeedHeaderDropdownType.POPULAR_FEED_TIME_GRANULARITY, isOpen);
	};

	const handleFollowingFeedSortByDropdownOpenChange = (isOpen: boolean) => {
		handleDropdownOpenChange(FeedHeaderDropdownType.FOLLOWING_FEED_SORT_BY, isOpen);
	};

	const followingFeedSortByDropdownText = getFollowingFeedSortByDropDownText();
	const popularFeedTimeGranularityDropdownText = getPopularFeedTimeGranularityDropDownText();

	const renderFollowingFeedSortByItems = () => (
		<>
			<DropdownItem
				isSelected={followingFeedSortBy === FollowingFeedSortByValue.Relevant}
				onClick={() =>
					handleDropdownChange(
						FeedHeaderDropdownType.FOLLOWING_FEED_SORT_BY,
						FollowingFeedSortByValue.Relevant,
					)
				}
			>
				<FormattedMessage {...i18n.sortByRelevant} />
			</DropdownItem>
			<DropdownItem
				isSelected={followingFeedSortBy === FollowingFeedSortByValue.Recent}
				onClick={() =>
					handleDropdownChange(
						FeedHeaderDropdownType.FOLLOWING_FEED_SORT_BY,
						FollowingFeedSortByValue.Recent,
					)
				}
			>
				<FormattedMessage {...i18n.sortByRecent} />
			</DropdownItem>
		</>
	);

	const renderFollowingFeedSortByDropdown = () => (
		<DropdownMenu
			onOpenChange={(onOpenChangeAttributes) =>
				handleFollowingFeedSortByDropdownOpenChange(onOpenChangeAttributes.isOpen)
			}
			trigger={followingFeedSortByDropdownText}
			shouldRenderToParent
		>
			<DropdownItemGroup>{renderFollowingFeedSortByItems()}</DropdownItemGroup>
		</DropdownMenu>
	);

	const renderPopularFeedTimeGranularityDropdown = () => (
		<Box testId="popular-feed-dropdown">
			<DropdownMenu
				onOpenChange={(onOpenChangeAttributes) =>
					handlePopularFeedTimeGranularityDropdownOpenChange(onOpenChangeAttributes.isOpen)
				}
				trigger={popularFeedTimeGranularityDropdownText}
				shouldRenderToParent
			>
				<DropdownItemGroup>
					<DropdownItem
						isSelected={popularFeedTimeGranularity === PopularFeedTimeGranularityValue.Day}
						onClick={() =>
							handleDropdownChange(
								FeedHeaderDropdownType.POPULAR_FEED_TIME_GRANULARITY,
								PopularFeedTimeGranularityValue.Day,
							)
						}
					>
						<FormattedMessage {...i18n.day} />
					</DropdownItem>
					<DropdownItem
						isSelected={popularFeedTimeGranularity === PopularFeedTimeGranularityValue.Week}
						onClick={() =>
							handleDropdownChange(
								FeedHeaderDropdownType.POPULAR_FEED_TIME_GRANULARITY,
								PopularFeedTimeGranularityValue.Week,
							)
						}
					>
						<FormattedMessage {...i18n.week} />
					</DropdownItem>
					<DropdownItem
						isSelected={popularFeedTimeGranularity === PopularFeedTimeGranularityValue.Default}
						onClick={() =>
							handleDropdownChange(
								FeedHeaderDropdownType.POPULAR_FEED_TIME_GRANULARITY,
								PopularFeedTimeGranularityValue.Default,
							)
						}
					>
						<FormattedMessage {...i18n.twoWeeks} />
					</DropdownItem>
					<DropdownItem
						isSelected={popularFeedTimeGranularity === PopularFeedTimeGranularityValue.Month}
						onClick={() =>
							handleDropdownChange(
								FeedHeaderDropdownType.POPULAR_FEED_TIME_GRANULARITY,
								PopularFeedTimeGranularityValue.Month,
							)
						}
					>
						<FormattedMessage {...i18n.month} />
					</DropdownItem>
					<DropdownItem
						isSelected={popularFeedTimeGranularity === PopularFeedTimeGranularityValue.Year}
						onClick={() =>
							handleDropdownChange(
								FeedHeaderDropdownType.POPULAR_FEED_TIME_GRANULARITY,
								PopularFeedTimeGranularityValue.Year,
							)
						}
					>
						<FormattedMessage {...i18n.year} />
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
		</Box>
	);

	const renderFollowingFeedActions = () => (
		<Inline space="space.100">
			{renderFollowingFeedSortByDropdown()}
			<Button onClick={handleManageButtonClick} testId="manage-feed-button">
				<FormattedMessage {...i18n.manageFeed} />
			</Button>
		</Inline>
	);

	const shouldRenderActions = () =>
		!(feedType === FeedType.FOLLOWING && hasFollowedAnyone === false && !hasResults) &&
		((feedType === FeedType.POPULAR && !fg('confluence_ai_feeds_home_banner')) ||
			feedType === FeedType.FOLLOWING);

	const renderActions = () => {
		if (!shouldRenderActions()) {
			return null;
		}

		if (feedType === FeedType.POPULAR && !fg('confluence_ai_feeds_home_banner')) {
			return renderPopularFeedTimeGranularityDropdown();
		} else if (
			feedType === FeedType.FOLLOWING &&
			expValEquals('cc_smarts_following_feed_dropdown_experiment', 'cohort', 'test')
		) {
			return renderFollowingFeedActions();
		} else if (feedType === FeedType.FOLLOWING) {
			return (
				<OldButton
					theme={linkButtonTheme}
					onClick={handleManageButtonClick}
					testId="manage-feed-button"
				>
					<FormattedMessage {...i18n.manageFeed} />
				</OldButton>
			);
		}
	};

	return (
		<FeedTitleAndActions data-testid="feed-header">
			<SpotlightContainer />
			<FeedTitleHeader>
				<Heading size="xsmall" as="h2" id="tablist-feedtypes">
					<FormattedMessage {...i18n.feedTitle} />
				</Heading>
				{renderActions()}
			</FeedTitleHeader>
			<SelectorAndActions>
				{renderPopularFeed && (
					<FeedTypeSelector
						selectedFeedType={feedType}
						setSelectedFeedType={setFeedType}
						customPanels={customPanels}
						popularFeedPillRef={popularFeedPillRef}
						followingFeedPillRef={followingFeedPillRef}
					/>
				)}
			</SelectorAndActions>
		</FeedTitleAndActions>
	);
};
